import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2, LogIn, Sparkles, ShieldCheck, ArrowLeft, Mail, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ASSETS, SCHOOL_INFO } from "@/lib/assets";

const STAFF_ROLES = ["super_admin", "founder", "finance", "admin", "teacher", "scolarite", "director", "nurse", "canteen"];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyOtp } = useAuth();
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Session expirée (redirigé par l'intercepteur API 401)
  useEffect(() => {
    if (new URLSearchParams(location.search).get("session") === "expiree") {
      toast.info("Votre session a expiré. Veuillez vous reconnecter.", { duration: 6000 });
    }
  }, []);

  // OTP state
  const [pendingId, setPendingId] = useState("");
  const [destMasked, setDestMasked] = useState("");
  const [otpChannel, setOtpChannel] = useState("email");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step === "otp" && codeInputRef.current) codeInputRef.current.focus();
  }, [step]);

  const submitCreds = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Email/téléphone et mot de passe requis");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.require_otp) {
        setPendingId(res.pending_id);
        setDestMasked(res.destination_masked || email);
        setOtpChannel(res.channel || "email");
        setDevOtp(res.dev_otp || "");
        setStep("otp");
        if (res.dev_otp) {
          toast.info(`Mode test — code : ${res.dev_otp}`);
        } else {
          toast.success(res.channel === "sms" ? "Un code a été envoyé par SMS." : "Un code a été envoyé sur votre adresse email.");
        }
      } else {
        if (STAFF_ROLES.includes(res.user.role)) {
          toast.error("Ce compte appartient au personnel. Veuillez utiliser le portail dédié.");
          navigate("/admin-portal");
          return;
        }
        toast.success("Bienvenue !");
        navigate(location.state?.from || "/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (!code || code.length !== 6) return toast.error("Saisissez le code à 6 chiffres");
    setLoading(true);
    try {
      const user = await verifyOtp(pendingId, code);
      if (STAFF_ROLES.includes(user.role)) {
        toast.error("Ce compte appartient au personnel. Veuillez utiliser le portail dédié.");
        navigate("/admin-portal");
        return;
      }
      toast.success("Connexion validée !");
      navigate(location.state?.from || "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const backToCreds = () => {
    setStep("credentials");
    setCode("");
    setPendingId("");
  };

  const quickLogin = undefined;
  void quickLogin;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="page-login">
      {/* Left - visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#166534] to-[#1E3A8A] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-dot-pattern" />
        <Link to="/" className="flex items-center gap-3 relative z-10">
          <img src={ASSETS.logo} alt="" className="h-12 w-12 rounded-full" />
          <div>
            <div className="font-heading font-extrabold text-lg">LES ÉCOLES MAZID</div>
            <div className="text-[10px] tracking-[0.25em] text-[#FBBF24] uppercase">{SCHOOL_INFO.tagline}</div>
          </div>
        </Link>
        <div className="relative z-10">
          <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-6"><Sparkles className="h-3 w-3" /><span>Mazid School Connect</span></div>
          <h2 className="mazid-headline text-5xl mb-4">L&apos;école, à portée de clic.</h2>
          <p className="text-slate-200 leading-relaxed mb-8">Suivez la scolarité de vos enfants, payez les frais, consultez les notes et échangez avec les enseignants — depuis n&apos;importe où.</p>

          {step === "credentials" && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 max-w-md">
              <ShieldCheck className="h-7 w-7 text-[#FBBF24] mb-3" />
              <div className="font-heading font-bold text-lg mb-1">Espace sécurisé</div>
              <p className="text-sm text-slate-200">Connexion protégée par vérification en deux étapes. Vos données scolaires sont chiffrées et confidentielles.</p>
            </div>
          )}
          {step === "otp" && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 max-w-md">
              <ShieldCheck className="h-7 w-7 text-[#FBBF24] mb-3" />
              <div className="font-heading font-bold text-lg mb-1">Vérification en deux étapes</div>
              <p className="text-sm text-slate-200">Pour sécuriser votre compte, nous envoyons un code unique à chaque connexion.</p>
            </div>
          )}
        </div>
      </div>

      {/* Right - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <img src={ASSETS.logo} alt="" className="h-10 w-10 rounded-full" />
            <div className="font-heading font-extrabold text-[#1E3A8A]">LES ÉCOLES MAZID</div>
          </Link>

          {step === "credentials" && (
            <>
              <h1 className="font-heading text-4xl font-extrabold text-[#1E3A8A] mb-2 tracking-tight">Connexion</h1>
              <p className="text-slate-600 mb-8">Accédez à votre espace Mazid School Connect.</p>
              <form onSubmit={submitCreds} className="space-y-5">
                <div>
                  <Label htmlFor="login-email">Email, téléphone ou matricule</Label>
                  <Input id="login-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@example.com, 6XX XX XX XX ou MZD-26-00001" data-testid="login-email" />
                </div>
                <div>
                  <Label htmlFor="login-pass">Mot de passe</Label>
                  <PasswordInput id="login-pass" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#166534] hover:bg-[#14532d] text-white gap-2 py-6" data-testid="login-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" /> Continuer</>}
                </Button>
              </form>

              {/* Connexion Google (Emergent managed) */}
              <div className="mt-4">
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 uppercase">ou</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full py-6 gap-3 border-slate-300 hover:bg-slate-50 font-semibold"
                  data-testid="google-login-btn"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + "/dashboard";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  Se connecter avec Google
                </Button>
              </div>

              <div className="mt-8 text-center">
                <span className="text-sm text-slate-600">Pas encore de compte ? </span>
                <Link to="/inscription" className="text-sm font-semibold text-[#EA580C] hover:underline" data-testid="login-to-register">Inscrire un enfant</Link>
                <span className="text-sm text-slate-400 mx-2">·</span>
                <Link to="/inscription-eleve" className="text-sm font-semibold text-[#1E3A8A] hover:underline" data-testid="login-to-student">Élève : créer mon compte</Link>
              </div>
            </>
          )}

          {step === "otp" && (
            <>
              <button onClick={backToCreds} className="text-xs text-slate-500 hover:text-[#1E3A8A] mb-4 flex items-center gap-1" data-testid="otp-back-btn">
                <ArrowLeft className="h-3 w-3" /> Retour
              </button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#166534] flex items-center justify-center mb-5">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-[#1E3A8A] mb-2 tracking-tight">Vérification de sécurité</h1>
              <p className="text-slate-600 mb-2">Saisissez le code à 6 chiffres reçu par {otpChannel === "sms" ? "SMS" : "email"} :</p>
              <p className="text-sm text-slate-500 mb-6 flex items-center gap-1">{otpChannel === "sms" ? <Smartphone className="h-3 w-3" /> : <Mail className="h-3 w-3" />} envoyé à <strong>{destMasked}</strong></p>

              {devOtp && (
                <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="dev-otp-hint">
                  <strong>Mode test :</strong> votre code est <code className="font-mono font-bold text-base">{devOtp}</code>. <br/>En production, ce code arrivera uniquement par {otpChannel === "sms" ? "SMS" : "email"}.
                </div>
              )}

              <form onSubmit={submitOtp} className="space-y-5">
                <div>
                  <Label htmlFor="otp-code">Code à 6 chiffres</Label>
                  <Input
                    ref={codeInputRef}
                    id="otp-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="•   •   •   •   •   •"
                    className="text-center text-2xl tracking-[0.5em] font-bold h-14"
                    data-testid="otp-code-input"
                  />
                </div>
                <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-[#166534] hover:bg-[#14532d] text-white gap-2 py-6 disabled:opacity-50" data-testid="otp-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Vérifier et se connecter</>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
